import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import HeroWithBackground from 'Blocks/HeroWithBackground';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const StyledHeroWithBackground = styled(HeroWithBackground)`
  margin-top: 36px;
  ${legacyTheme.breakpoints.up('md')} {
    margin-top: 144px;
  }
`;

const Container = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    max-width: 420px;
  }
`;

const Divider = styled.div`
  width: 100%;
  background-color: black;
  height: 0.5px;
`;

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

// We need to do this because the png has a lot of space around the icon
const StyledImage = styled(Image)`
  margin-left: -32px;
`;

const MobileWrapper = styled.div`
  background-color: ${theme.colors.neutral['600']};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const StyledMobileImage = styled(Image)`
  width: 100%;
  height: auto;
`;

const MobileTextWrapper = styled.div`
  padding: 35px;
`;

const StyledTitle = styled(Typography)`
  color: ${theme.colors.primary[400]};
`;

const SocialImpactHero = ({ backgroundImages, description, title, logos }) => {
  const { isMobile } = useResponsiveVariant();

  const children = (
    <>
      <StyledTitle align="left" variant={isMobile ? 'h1' : 'hero2'}>
        {title}
      </StyledTitle>
      <Spacer size={isMobile ? 9 : 20} />
      <Typography align="left" color="noir" variant="p1">
        {description}
      </Typography>
      <Spacer size={isMobile ? 9 : 28} />
      {!isMobile && (
        <>
          <Divider />
          <Spacer size={35} />
        </>
      )}
      <ImageWrapper>
        {logos.map(logo => (
          <StyledImage key={logo.alt} alt={logo.alt} height="auto" src={logo.src} width={130} />
        ))}
      </ImageWrapper>
    </>
  );
  if (isMobile) {
    return (
      <MobileWrapper>
        <MobileTextWrapper>{children}</MobileTextWrapper>
        <StyledMobileImage alt="woman with bambou in front of her" src={backgroundImages.mobile} />
      </MobileWrapper>
    );
  }
  return (
    <StyledHeroWithBackground
      backgroundPosition="center bottom"
      content={{ imageDesktop: backgroundImages.desktop, imageMobile: backgroundImages.mobile }}
      height={isMobile ? `calc(100vh - ${legacyTheme.props.headerHeight})` : null}
    >
      <Container>{children}</Container>
    </StyledHeroWithBackground>
  );
};

SocialImpactHero.propTypes = {
  backgroundImages: PropTypes.shape({
    desktop: srcOrNextImageProp,
    mobile: srcOrNextImageProp,
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  logos: PropTypes.arrayOf(PropTypes.shape({ src: srcOrNextImageProp, alt: PropTypes.string }))
    .isRequired,
};

export default SocialImpactHero;
